import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@mui/material';
import { useAtom } from 'jotai';
import { isObject, sortBy } from 'lodash';
import { useMemo, useRef } from 'react';
import { useLocation } from 'react-router';

import { PAGE_SIZES } from '@/constants/common';
import type { Asset } from '@/pages/Assets/Assets.types';
import selectedVulnerabilityAtom from '@/pages/VulnerabilityManagement/VMPVulnerabilities/VMPVulnerabilities.store';
import {
  vmpAssetsTablePageAtom,
  vmpTableOrderAssetsAtom,
  vmpTableOrderByAssetsAtom
} from '@/pages/VulnerabilityManagement/VulnerabilityManagement.store';
import { replaceUnwantedValues } from '@/pages/VulnerabilityManagement/VulnerabilityManagement.utils';
import { sortIpAddresses } from '@/utils/Common.utils';

import {
  AFFECTED_ASSETS_COLUMNS,
  REMEDIATED_ASSETS_COLUMNS
} from './VulnerabilityDetailsAssetTable.constants';
import useStyles from './VulnerabilityDetailsAssetTable.styles';

const VulnerabilityDetailsAssetTable = ({
  remediated,
  rowsPerPage,
  setRowsPerPage
}: {
  remediated?: boolean;
  rowsPerPage: number;
  setRowsPerPage: (value: number) => void;
}) => {
  const [selectedVulnerability] = useAtom(selectedVulnerabilityAtom);
  const [page, setPage] = useAtom(vmpAssetsTablePageAtom);
  const [order, setOrder] = useAtom(vmpTableOrderAssetsAtom);
  const [orderBy, setOrderBy] = useAtom(vmpTableOrderByAssetsAtom);
  const { pathname } = useLocation();
  const ref = useRef<HTMLTableSectionElement>(null);
  const { classes } = useStyles();

  const handleChangePage = (
    _event: React.MouseEvent | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPage(page);
    setRowsPerPage(Number(event.target.value));
  };

  const handleRequestSort = (property: string) => () => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const assets = useMemo(() => {
    const selectedVulnerabilityAssets: Asset[] =
      (remediated
        ? selectedVulnerability?.remediated_assets
        : selectedVulnerability?.assets
      )?.map((asset: Asset) => ({
        ...asset,
        key: `${asset.id}-${asset.asset_name}`
      })) || [];

    return (
      (replaceUnwantedValues(selectedVulnerabilityAssets) as Asset[]) || []
    );
  }, [pathname, selectedVulnerability]);

  const visibleRows: Asset[] = useMemo(() => {
    let orderProperty = orderBy;
    if (orderProperty === 'ip_address') {
      const sortedIps = sortIpAddresses(assets) as Asset[];
      let slicedIps = order === 'asc' ? sortedIps : sortedIps.reverse();
      slicedIps = slicedIps.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      );
      return slicedIps.filter(
        (item): item is Asset =>
          typeof item === 'object' && item !== null && 'id' in item
      );
    }

    if (
      assets &&
      assets.length > 0 &&
      isObject((assets[0] as Record<string, unknown>)[orderBy])
    ) {
      orderProperty = `${orderBy}.name`;
    }

    let result = sortBy(assets, orderProperty);

    if (order === 'desc') {
      result = result.reverse();
    }
    result = result.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return result.filter(
      (item): item is Asset =>
        typeof item === 'object' && item !== null && 'id' in item
    );
  }, [order, orderBy, page, rowsPerPage, assets]);

  return (
    <TableContainer ref={ref} className={classes.assetsTableContainer}>
      {assets.length === 0 && <Box className={classes.noData}>No data</Box>}
      <Table stickyHeader className={classes.assetsTable}>
        <TableHead>
          <TableRow>
            {(remediated
              ? REMEDIATED_ASSETS_COLUMNS
              : AFFECTED_ASSETS_COLUMNS
            ).map((column, index) => (
              <TableCell
                align="left"
                style={{
                  paddingLeft: index === 0 ? 16 : '',
                  height: 60
                }}
                className={classes.assetsTableHeaderCell}
                key={column.id}
                width={column.width || 'auto'}
              >
                <TableSortLabel
                  active={orderBy === column.id}
                  direction={orderBy === column.id ? order : 'asc'}
                  onClick={handleRequestSort(column.id)}
                >
                  {column.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleRows.map((row) => (
            <TableRow key={row.key} sx={{ cursor: 'pointer', height: 50 }}>
              {(remediated
                ? REMEDIATED_ASSETS_COLUMNS
                : AFFECTED_ASSETS_COLUMNS
              ).map((column, index) => (
                <TableCell
                  className={classes.tableBodyCell}
                  align="left"
                  key={column.id}
                  style={{
                    paddingLeft: index === 0 ? 16 : ''
                  }}
                >
                  {column.cell
                    ? column.cell(row)
                    : (row[column.id as keyof typeof row] as string)}
                </TableCell>
              ))}
            </TableRow>
          ))}

          {/* empty row to fill the table height */}
          {visibleRows.length < rowsPerPage && (
            <TableRow sx={{ height: '100%' }}>
              <TableCell colSpan={6} sx={{ padding: 0, border: 'none' }} />
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              className={classes.assetsTablePagination}
              rowsPerPageOptions={PAGE_SIZES}
              count={assets.length}
              rowsPerPage={rowsPerPage}
              page={rowsPerPage * page > assets.length ? 0 : page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

export default VulnerabilityDetailsAssetTable;
